import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { usePageLoading } from '../contexts/PageLoadingContext';

/**
 * Formata data do formato ISO para o formato português "11 de Mar de 2026"
 * Usa métodos UTC para evitar problemas de timezone
 */
function formatDateLong(dateStr: string): string {
  if (!dateStr) return '';
  
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const date = new Date(dateStr + 'T12:00:00');
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  
  return `${day} de ${month} de ${year}`;
}

/**
 * Estrutura de dados retornada pelo endpoint /consumo/energia/:id/api_data
 */
interface ApiDataResponse {
  customer_id?: string;
  meter_types: string[];
  resume?: {
    last_statement: string;
    next_statement: string;
    days_count: number;
    current_usage: number;
    forecast: number;
    current_month_usage_avg: number;
    monthly_consumption_avg: number;
  };
  usage_today?: number;
  goal_status?: 'Abaixo' | 'Dentro' | 'Ultrapassou';
  monthly_labels?: string[];
  monthly_consumption?: number[];
  daily_usage?: Array<{ date: string; meter_type: string; usage: number }>;
  last_statements?: Array<{ period: string; consumption: number }>;
  /** true quando a unidade não tem medidor deste tipo (ver ConsumptionDataController). */
  no_data?: boolean;
}

/**
 * Dados processados para a EnergyHomePage
 */
export interface EnergyConsumptionData {
  // Dados do dia
  usageToday: number;
  currentMonthUsageAvg: number;
  
  // Dados do mês
  currentUsage: number;
  monthlyConsumptionAvg: number;
  forecast: number;
  
  // Meta
  goalStatus: 'Abaixo' | 'Dentro' | 'Ultrapassou';
  hasGoal: boolean;
  goalValue: number;
  
  // Próximo fechamento
  nextStatement: string;
  beginningOfPeriod: string;
  
  // Gráfico mensal (últimos 6 meses)
  monthlyLabels: string[];
  monthlyConsumption: number[];
  
  // Gráfico diário
  dailyLabels: number[];
  energyDaily: number[];
  
  // Tipos de medidor
  meterTypes: string[];
}

interface UseEnergyConsumptionResult {
  data: EnergyConsumptionData | null;
  isLoading: boolean;
  error: string | null;
  /** true quando a unidade não tem medidor de energia cadastrado (não é um erro). */
  noData: boolean;
  refetch: () => void;
}

/**
 * Hook para buscar dados de consumo de energia de uma residência.
 *
 * Chama o endpoint: GET /consumo/energia/:rgi/api_data
 *
 * @param residenceId - ID da residência (RGI)
 */
export function useEnergyConsumption(residenceId: string): UseEnergyConsumptionResult {
  const [data, setData] = useState<EnergyConsumptionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noData, setNoData] = useState(false);
  const { startPageLoading, stopPageLoading } = usePageLoading();

  // Esconde o preloader apenas APÓS o React processar isLoading = false
  useEffect(() => {
    if (!isLoading) {
      stopPageLoading();
    }
  }, [isLoading, stopPageLoading]);

  const fetchData = useCallback(async () => {
    if (!residenceId) {
      setError('ID da residência não fornecido');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    startPageLoading();
    setError(null);
    setNoData(false);

    try {
      const response = await api.get<ApiDataResponse>(
        `/consumo/energia/${residenceId}/api_data`
      );

      const apiData = response.data;

      if (apiData.no_data) {
        setData(null);
        setNoData(true);
        return;
      }

      // Extrai dados do consumo diário de energia
      const dailyUsage = apiData.daily_usage || [];
      const energyData = dailyUsage.filter(d => d.meter_type === 'ENERGIA');

      const dailyLabels = energyData.map(d => new Date(d.date).getDate());
      const energyDaily = energyData.map(d => d.usage);

      const resume = apiData.resume || {} as NonNullable<ApiDataResponse['resume']>;

      const processedData: EnergyConsumptionData = {
        usageToday: apiData.usage_today || 0,
        currentMonthUsageAvg: resume.current_month_usage_avg,
        currentUsage: resume.current_usage,
        monthlyConsumptionAvg: resume.monthly_consumption_avg,
        forecast: resume.forecast,
        goalStatus: apiData.goal_status || 'Dentro',
        hasGoal: false,
        goalValue: resume.monthly_consumption_avg,
        nextStatement: resume.next_statement,
        beginningOfPeriod: formatDateLong(resume.last_statement),
        monthlyLabels: apiData.monthly_labels || [],
        monthlyConsumption: apiData.monthly_consumption || [],
        dailyLabels,
        energyDaily,
        meterTypes: apiData.meter_types,
      };

      setData(processedData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar dados de consumo';
      setError(errorMessage);
      console.error('useEnergyConsumption error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [residenceId, startPageLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    noData,
    refetch: fetchData,
  };
}
